import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapCustomizationProjectList,
  mapCustomizationCandidateList,
  mapCustomizationReassignResult,
  mapFollowupCreatePayload,
  mapFollowupListPage,
  mapFollowupRecord,
  mapOpportunityContactOption,
  mapOpportunityCreatePayload,
  mapOpportunityCustomerOption,
  mapOpportunityWorkbenchItem,
  mapOpportunityListPage,
  mapOpportunityListQueryParams,
  mapOpportunityMetadata,
  mapOpportunityOrganizationOption,
  mapOpportunityOwnerReassignPayload,
  mapOpportunityOwnerReassignResult,
  mapOpportunityQuickCreateCheck,
  mapOpportunitySplitPayload,
  mapOpportunitySplitPreview,
  mapOpportunitySplitResult,
  mapSignInfoDetail,
  mapSignInfoSavePayload,
  mapStageLogList,
  mapStageBoard,
  mapStageOwnerBulkAssignPayload,
  mapStageOwnerList,
  type OpportunityContactOptionRaw,
  type OpportunityCustomerOptionRaw,
  type OpportunityCustomizationProjectListRaw,
  type OpportunityCustomizationCandidateListRaw,
  type OpportunityCustomizationReassignResultRaw,
  type OpportunityFollowupListPageRaw,
  type OpportunityFollowupRecordRaw,
  type OpportunityOrganizationOptionRaw,
  type OpportunitySplitPreviewRaw,
  type OpportunitySplitResultRaw,
  type OpportunityStageLogListRaw,
  type OpportunityStageBoardRaw,
  type OpportunityStageOwnerListRaw,
  type OpportunityWorkbenchListPageRaw,
  type OpportunityWorkbenchItemRaw,
  type OpportunityWorkbenchMetadataRaw,
  type OpportunityOwnerReassignResultRaw,
  type OpportunityQuickCreateCheckRaw,
} from "../api/adapters";
import type {
  OpportunityContactOption,
  OpportunityCreatePayload,
  OpportunityCustomerOption,
  OpportunityCustomizationProjectList,
  OpportunityCustomizationCandidateList,
  OpportunityCustomizationReassignResult,
  OpportunityFollowupCreatePayload,
  OpportunityFollowupListPage,
  OpportunityFollowupRecord,
  OpportunityOrganizationOption,
  OpportunitySignInfoDetail,
  OpportunitySignInfoSavePayload,
  OpportunitySplitPayload,
  OpportunitySplitPreview,
  OpportunitySplitResult,
  OpportunityStageLogList,
  OpportunityStageBoard,
  OpportunityManualStagePayload,
  OpportunityStageOwnerBulkAssignPayload,
  OpportunityStageOwnerList,
  OpportunityWorkbenchListParams,
  OpportunityWorkbenchListPage,
  OpportunityWorkbenchItem,
  OpportunityWorkbenchMetadata,
  OpportunityOwnerReassignPayload,
  OpportunityOwnerReassignResult,
  OpportunityQuickCreateCheck,
} from "../types";

/** 查询商机工作台元数据。 */
export async function fetchOpportunityMetadata(): Promise<OpportunityWorkbenchMetadata> {
  const response = await httpClient.get<ApiEnvelope<OpportunityWorkbenchMetadataRaw>>(
    "/crm/opportunity-workbench/metadata",
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机工作台元数据接口返回了无效数据");
  }
  return mapOpportunityMetadata(response.data.data);
}

/** 搜索可建立商机的学校。 */
export async function searchOpportunityOrganizations({
  keyword,
  limit = 20,
}: {
  keyword?: string;
  limit?: number;
}): Promise<OpportunityOrganizationOption[]> {
  const response = await httpClient.get<ApiEnvelope<OpportunityOrganizationOptionRaw[]>>(
    "/crm/opportunity-workbench/organizations",
    {
      params: {
        keyword: normalizeOptionalText(keyword),
        limit,
      },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("学校候选接口返回了无效数据");
  }
  return response.data.data.map(mapOpportunityOrganizationOption);
}

/** 搜索指定学校下的客户候选。 */
export async function searchOpportunityCustomers({
  orgId,
  keyword,
  limit = 20,
}: {
  orgId: number;
  keyword?: string;
  limit?: number;
}): Promise<OpportunityCustomerOption[]> {
  const response = await httpClient.get<ApiEnvelope<OpportunityCustomerOptionRaw[]>>(
    `/crm/opportunity-workbench/organizations/${orgId}/customers`,
    {
      params: {
        keyword: normalizeOptionalText(keyword),
        limit,
      },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("客户候选接口返回了无效数据");
  }
  return response.data.data.map(mapOpportunityCustomerOption);
}

/** 搜索指定学校或客户下的联系人候选。 */
export async function searchOpportunityContacts({
  orgId,
  customerId,
  keyword,
  limit = 20,
}: {
  orgId: number;
  customerId?: number;
  keyword?: string;
  limit?: number;
}): Promise<OpportunityContactOption[]> {
  const response = await httpClient.get<ApiEnvelope<OpportunityContactOptionRaw[]>>(
    `/crm/opportunity-workbench/organizations/${orgId}/contacts`,
    {
      params: {
        customer_id: customerId,
        keyword: normalizeOptionalText(keyword),
        limit,
      },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人候选接口返回了无效数据");
  }
  return response.data.data.map(mapOpportunityContactOption);
}

/** 查询商机分页列表。 */
export async function fetchOpportunityList(
  params: OpportunityWorkbenchListParams,
): Promise<OpportunityWorkbenchListPage> {
  const response = await httpClient.get<ApiEnvelope<OpportunityWorkbenchListPageRaw>>(
    "/crm/opportunity-workbench/opportunities",
    {
      params: mapOpportunityListQueryParams(params),
      // FastAPI 期望 stages=a&stages=b；axios 默认会序列化为 stages[]=，导致阶段筛选失效。
      paramsSerializer: { indexes: null },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机列表接口返回了无效数据");
  }
  return mapOpportunityListPage(response.data.data);
}

/** 新增商机。 */
export async function createOpportunity(
  payload: OpportunityCreatePayload,
): Promise<OpportunityWorkbenchItem> {
  const response = await httpClient.post<ApiEnvelope<OpportunityWorkbenchItemRaw>>(
    "/crm/opportunity-workbench/opportunities",
    mapOpportunityCreatePayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机新增接口返回了无效数据");
  }
  return mapOpportunityWorkbenchItem(response.data.data);
}

/** 检查联系人动态是否允许快捷建商机。 */
export async function checkQuickCreateOpportunity(
  contactId: number,
): Promise<OpportunityQuickCreateCheck> {
  const response = await httpClient.get<ApiEnvelope<OpportunityQuickCreateCheckRaw>>(
    "/crm/opportunity-workbench/opportunities/quick-create-check",
    { params: { contact_id: contactId } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("快捷建商机校验接口返回了无效数据");
  }
  return mapOpportunityQuickCreateCheck(response.data.data);
}

/** 按严格去重结果创建一个快捷商机。 */
export async function createQuickOpportunity({
  contactId,
  travelMonth,
}: {
  contactId: number;
  travelMonth: string;
}): Promise<OpportunityWorkbenchItem> {
  const response = await httpClient.post<ApiEnvelope<OpportunityWorkbenchItemRaw>>(
    "/crm/opportunity-workbench/opportunities/quick-create",
    { contact_id: contactId, travel_month: travelMonth },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("快捷建商机接口返回了无效数据");
  }
  return mapOpportunityWorkbenchItem(response.data.data);
}

/** 批量转派商机主负责人。 */
export async function reassignOpportunityOwners(
  payload: OpportunityOwnerReassignPayload,
): Promise<OpportunityOwnerReassignResult> {
  const response = await httpClient.post<ApiEnvelope<OpportunityOwnerReassignResultRaw>>(
    "/crm/opportunity-workbench/opportunities/reassign-owner",
    mapOpportunityOwnerReassignPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机转派接口返回了无效数据");
  }
  return mapOpportunityOwnerReassignResult(response.data.data);
}

/** 编辑商机。 */
export async function updateOpportunity({
  opportunityId,
  payload,
}: {
  opportunityId: number;
  payload: OpportunityCreatePayload;
}): Promise<OpportunityWorkbenchItem> {
  const response = await httpClient.put<ApiEnvelope<OpportunityWorkbenchItemRaw>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}`,
    mapOpportunityCreatePayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机编辑接口返回了无效数据");
  }
  return mapOpportunityWorkbenchItem(response.data.data);
}

/** 停用商机。 */
export async function disableOpportunity(opportunityId: number): Promise<boolean> {
  const response = await httpClient.post<ApiEnvelope<{ deleted: boolean }>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/disable`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机停用接口返回了无效数据");
  }
  return response.data.data.deleted;
}

/** 启用已停用商机。 */
export async function enableOpportunity(opportunityId: number): Promise<boolean> {
  const response = await httpClient.post<ApiEnvelope<{ deleted: boolean }>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/enable`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机启用接口返回了无效数据");
  }
  return response.data.data.deleted;
}

/** 查询商机跟进记录。 */
export async function fetchOpportunityFollowupRecords({
  opportunityId,
  page,
  pageSize,
}: {
  opportunityId: number;
  page: number;
  pageSize: number;
}): Promise<OpportunityFollowupListPage> {
  const response = await httpClient.get<ApiEnvelope<OpportunityFollowupListPageRaw>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/followup-records`,
    {
      params: {
        page,
        page_size: pageSize,
      },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机跟进记录接口返回了无效数据");
  }
  return mapFollowupListPage(response.data.data);
}

/** 新增商机跟进记录。 */
export async function createOpportunityFollowupRecord({
  opportunityId,
  payload,
}: {
  opportunityId: number;
  payload: OpportunityFollowupCreatePayload;
}): Promise<OpportunityFollowupRecord> {
  const response = await httpClient.post<ApiEnvelope<OpportunityFollowupRecordRaw>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/followup-records`,
    mapFollowupCreatePayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机跟进记录新增接口返回了无效数据");
  }
  return mapFollowupRecord(response.data.data);
}

/** 删除商机跟进记录。 */
export async function deleteOpportunityFollowupRecord({
  opportunityId,
  recordId,
}: {
  opportunityId: number;
  recordId: number;
}): Promise<boolean> {
  const response = await httpClient.delete<ApiEnvelope<{ deleted: boolean }>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/followup-records/${recordId}`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机跟进记录删除接口返回了无效数据");
  }
  return response.data.data.deleted;
}

/** 查询商机输赢单。 */
export async function fetchOpportunitySignInfo(
  opportunityId: number,
): Promise<OpportunitySignInfoDetail> {
  const response = await httpClient.get<
    ApiEnvelope<{
      opportunity_id: number;
      status: string;
      is_sign?: boolean | null;
      reason_type?: string | null;
      busi_source_type?: string | null;
      team_summary?: string | null;
      reason_remark?: string | null;
      decision_maker_text?: string | null;
      created_by_user_id?: number | null;
      updated_by_user_id?: number | null;
      created_at?: string | null;
      updated_at?: string | null;
    }>
  >(`/crm/opportunity-workbench/opportunities/${opportunityId}/sign-info`);
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机输赢单接口返回了无效数据");
  }
  return mapSignInfoDetail(response.data.data);
}

/** 保存商机输赢单。 */
export async function saveOpportunitySignInfo({
  opportunityId,
  payload,
}: {
  opportunityId: number;
  payload: OpportunitySignInfoSavePayload;
}): Promise<OpportunitySignInfoDetail> {
  const response = await httpClient.put<
    ApiEnvelope<{
      opportunity_id: number;
      status: string;
      is_sign?: boolean | null;
      reason_type?: string | null;
      busi_source_type?: string | null;
      team_summary?: string | null;
      reason_remark?: string | null;
      decision_maker_text?: string | null;
      created_by_user_id?: number | null;
      updated_by_user_id?: number | null;
      created_at?: string | null;
      updated_at?: string | null;
    }>
  >(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/sign-info`,
    mapSignInfoSavePayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机输赢单保存接口返回了无效数据");
  }
  return mapSignInfoDetail(response.data.data);
}

/** 查询商机阶段历程。 */
export async function fetchOpportunityStageLogs(
  opportunityId: number,
): Promise<OpportunityStageLogList> {
  const response = await httpClient.get<ApiEnvelope<OpportunityStageLogListRaw>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/stage-logs`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机阶段历程接口返回了无效数据");
  }
  return mapStageLogList(response.data.data);
}

/** 查询商机 15 节点阶段线路图。 */
export async function fetchOpportunityStageBoard(
  opportunityId: number,
): Promise<OpportunityStageBoard> {
  const response = await httpClient.get<ApiEnvelope<OpportunityStageBoardRaw>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/stage-board`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机阶段线路图接口返回了无效数据");
  }
  return mapStageBoard(response.data.data);
}

/** 保存阶段 1-4 人工设置。 */
export async function saveOpportunityManualStage({
  opportunityId,
  payload,
}: { opportunityId: number; payload: OpportunityManualStagePayload }): Promise<OpportunityStageBoard> {
  const response = await httpClient.post<ApiEnvelope<OpportunityStageBoardRaw>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/manual-stage`,
    { stage_code: payload.stageCode, note: payload.note },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机人工阶段保存接口返回了无效数据");
  }
  return mapStageBoard(response.data.data);
}

/** 查询商机阶段负责人。 */
export async function fetchOpportunityStageOwners(
  opportunityId: number,
): Promise<OpportunityStageOwnerList> {
  const response = await httpClient.get<ApiEnvelope<OpportunityStageOwnerListRaw>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/stage-owners`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机阶段负责人接口返回了无效数据");
  }
  return mapStageOwnerList(response.data.data);
}

/** 保存商机阶段负责人设置。 */
export async function saveOpportunityStageOwners({
  opportunityId,
  payload,
}: {
  opportunityId: number;
  payload: OpportunityStageOwnerBulkAssignPayload;
}): Promise<OpportunityStageOwnerList> {
  const response = await httpClient.post<ApiEnvelope<OpportunityStageOwnerListRaw>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/stage-owners/bulk-assign`,
    mapStageOwnerBulkAssignPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机阶段负责人保存接口返回了无效数据");
  }
  return mapStageOwnerList(response.data.data);
}

/** 查询商机下定制项目与需求追溯摘要。 */
export async function fetchOpportunityCustomizationProjects(
  opportunityId: number,
): Promise<OpportunityCustomizationProjectList> {
  const response = await httpClient.get<ApiEnvelope<OpportunityCustomizationProjectListRaw>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/customization-projects`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机关联定制接口返回了无效数据");
  }
  return mapCustomizationProjectList(response.data.data);
}

/** 查询商机可能关联的定制候选。 */
export async function fetchOpportunityCustomizationCandidates(
  opportunityId: number,
): Promise<OpportunityCustomizationCandidateList> {
  const response = await httpClient.get<ApiEnvelope<OpportunityCustomizationCandidateListRaw>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/customization-candidates`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("找定制候选接口返回了无效数据");
  }
  return mapCustomizationCandidateList(response.data.data);
}

/** 批量把定制重新归类到目标商机。 */
export async function reassignOpportunityCustomizations({
  opportunityId,
  projectIds,
  expectedProjectUpdatedAt = {},
}: {
  opportunityId: number;
  projectIds: string[];
  expectedProjectUpdatedAt?: Record<string, string>;
}): Promise<OpportunityCustomizationReassignResult> {
  const response = await httpClient.post<ApiEnvelope<OpportunityCustomizationReassignResultRaw>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/customization-candidates/reassign`,
    {
      project_ids: projectIds,
      expected_project_updated_at: expectedProjectUpdatedAt,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("定制重新归类失败");
  }
  return mapCustomizationReassignResult(response.data.data);
}

/** 查询商机拆分预览。 */
export async function fetchOpportunitySplitPreview(
  opportunityId: number,
): Promise<OpportunitySplitPreview> {
  const response = await httpClient.get<ApiEnvelope<OpportunitySplitPreviewRaw>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/split-preview`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机拆分预览接口返回了无效数据");
  }
  return mapOpportunitySplitPreview(response.data.data);
}

/** 提交商机拆分。 */
export async function submitOpportunitySplit({
  opportunityId,
  payload,
}: {
  opportunityId: number;
  payload: OpportunitySplitPayload;
}): Promise<OpportunitySplitResult> {
  const response = await httpClient.post<ApiEnvelope<OpportunitySplitResultRaw>>(
    `/crm/opportunity-workbench/opportunities/${opportunityId}/split`,
    mapOpportunitySplitPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机拆分接口返回了无效数据");
  }
  return mapOpportunitySplitResult(response.data.data);
}

/** 归一可选搜索文本，避免空白字符串进入候选接口参数。 */
function normalizeOptionalText(value?: string) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}
